import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import styled, { keyframes } from 'styled-components';

// config
import { IMAGE_BASE_URL, POSTER_SIZE } from '../config';

// components
import BreadCrumb from './BreadCrumb';
import Grid from './Grid';
import Spinner from './Spinner';
import MovieInfo from './MovieInfo';
import MovieInfoBar from './MovieInfoBar';
import Actor from './Actor';

// hook
import { useMovieFetch } from '../hooks/useMovieFetch';
import { useUserAuth } from '../context/UserAuthContext';

// image
import NoImage from '../images/no_image.jpg';
import { Fragment } from 'react';
import { movieUrl, movieIdFromSlug } from '../helpers';

const SITE = 'https://mycinemadb.com';

// ─── Adult Interstitial Styles ────────────────────────────────────────────────

const fadeIn = keyframes`from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); }`;

const Gate = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const GateCard = styled.div`
  max-width: 440px;
  width: 100%;
  background: #1a1a1a;
  border: 1px solid rgba(255,80,80,0.2);
  border-radius: 18px;
  padding: 48px 40px;
  text-align: center;
  animation: ${fadeIn} 0.35s ease;
`;

const GateIcon = styled.div`
  font-size: 2.8rem;
  margin-bottom: 20px;
`;

const GateTitle = styled.h1`
  color: #fff;
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0 0 8px;
`;

const GateFilm = styled.p`
  color: rgba(255,255,255,0.4);
  font-size: 0.9rem;
  margin: 0 0 20px;
  font-style: italic;
`;

const GateWarning = styled.p`
  font-size: 0.88rem;
  color: rgba(255,120,120,0.85);
  background: rgba(255,80,80,0.08);
  border: 1px solid rgba(255,80,80,0.15);
  border-radius: 10px;
  padding: 12px 16px;
  margin: 0 0 28px;
  line-height: 1.6;
`;

const GateBtnRow = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
`;

const GateBtn = styled.button`
  padding: 11px 22px;
  border-radius: 99px;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  background: ${p => p.$primary ? '#fe88f1' : 'rgba(255,255,255,0.08)'};
  color: ${p => p.$primary ? '#0d0d0d' : 'rgba(255,255,255,0.6)'};
  &:hover { opacity: 0.85; transform: translateY(-1px); }
`;

const GateNote = styled.p`
  font-size: 0.76rem;
  color: rgba(255,255,255,0.25);
  margin: 20px 0 0;
  a { color: #fe88f1; text-decoration: none; }
`;

// ─── Adult Interstitial Component ─────────────────────────────────────────────

const AdultInterstitial = ({ title, isLoggedIn, onConfirm, onGoBack }) => (
  <Gate>
    <GateCard>
      <GateIcon>🔞</GateIcon>
      <GateTitle>Adult Content</GateTitle>
      <GateFilm>"{title}"</GateFilm>
      <GateWarning>
        This title is flagged as adult content by TMDB. You must be 18 or older to view it.
      </GateWarning>
      <GateBtnRow>
        <GateBtn onClick={onGoBack}>← Go Back</GateBtn>
        {isLoggedIn ? (
          <GateBtn $primary onClick={onConfirm}>I'm 18+ — Continue</GateBtn>
        ) : (
          <GateBtn $primary as="a" href="/sign-in">Sign in to view</GateBtn>
        )}
      </GateBtnRow>
      {!isLoggedIn && (
        <GateNote>
          Adult content requires a signed-in account. <a href="/sign-up">Create a free account</a>
        </GateNote>
      )}
    </GateCard>
  </Gate>
);

// ─── Movie Page ───────────────────────────────────────────────────────────────


const Movie = () => {
  const { movieSlug } = useParams();
  const navigate = useNavigate();
  const { isLoggedIn } = useUserAuth();

  // Support both "the-dark-knight-155" and legacy "155"
  const movieId = movieIdFromSlug(movieSlug);

  const { state: movie, loading, error } = useMovieFetch(movieId);

  // Track whether the user has clicked through the adult interstitial this session
  const [adultConfirmed, setAdultConfirmed] = useState(false);

  // Once movie data loads, redirect legacy numeric-only URLs to the slug URL
  useEffect(() => {
    if (!loading && movie.title && movieSlug === String(movieId)) {
      navigate(movieUrl(movieId, movie.title), { replace: true });
    }
  }, [loading, movie.title, movieId, movieSlug, navigate]);

  if (loading) return <Spinner />;
  if (error) return <div>Something went wrong...</div>;

  const canonicalSlug = movie.title ? movieUrl(movieId, movie.title) : `/movie/${movieId}`;
  const isAdult = !!movie.adult;

  // ── Soft interstitial gate for adult-flagged titles ──────────────────────────
  if (isAdult && !adultConfirmed) {
    return (
      <Fragment>
        {/* Tell Googlebot NOT to index this page */}
        <Helmet>
          <title>{movie.title} — MyCinemaDB</title>
          <meta name="robots" content="noindex,nofollow" />
        </Helmet>
        <AdultInterstitial
          title={movie.title}
          isLoggedIn={isLoggedIn}
          onConfirm={() => setAdultConfirmed(true)}
          onGoBack={() => navigate(-1)}
        />
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{movie.title} — MyCinemaDB</title>
        <meta name="description" content={movie.overview ? movie.overview.slice(0, 155) + '…' : `Discover ${movie.title} on MyCinemaDB.`} />
        <link rel="canonical" href={`${SITE}${canonicalSlug}`} />
        {/* Keep adult pages de-indexed even after the user confirms */}
        {isAdult && <meta name="robots" content="noindex,nofollow" />}
      </Helmet>
      <BreadCrumb movieTitle={movie.original_title} />
      <MovieInfo movie={movie} />
      <MovieInfoBar
        time={movie.runtime}
        budget={movie.budget}
        revenue={movie.revenue}
      />
      <Grid header='Actors'>
        {(movie.actors || []).map(actor => (
          <Actor
            key={actor.credit_id}
            actorId={actor.id}
            name={actor.name}
            character={actor.character}
            imageUrl={
              actor.profile_path
                ? `${IMAGE_BASE_URL}${POSTER_SIZE}${actor.profile_path}`
                : NoImage
            }
          />
        ))}
      </Grid>
    </Fragment>
  );
};

export default Movie;